"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  DEFAULT_SORT_KEY,
  availableSortOptions,
  sortQuestions,
  type SortKey,
} from "@/lib/practiceSort";
import {
  ALL_QUESTIONS,
  GENERAL_QUESTIONS,
  filterQuestionsByCareerStage,
  type Question,
} from "@/lib/questions";
import { getJobTypeLabel } from "@/lib/jobTypes";
import { buildPracticeUrl } from "@/lib/practiceFilterParams";
import { useAuth } from "@/lib/auth";
import apiClient from "@/lib/api";
import { usePracticeLimit } from "@/hooks/usePracticeLimit";
import { useCareerStage } from "@/hooks/useCareerStage";
import { useQuestionFilters, getDisplayCategory } from "@/hooks/useQuestionFilters";
import {
  canShowProfileNudge,
  snoozeProfileNudge,
} from "@/lib/profileNudgeSnooze";
import PracticeLimitModal from "./PracticeLimitModal";
import ProfileNudgeModal from "./ProfileNudgeModal";
import QuestionFilterPanel from "@/components/shared/QuestionFilterPanel";
import { UniversityBadge } from "@/components/shared/QuestionBadges";
import { useUniversityQuestions } from "@/hooks/useUniversityQuestions";
import type { UniversitySelectable } from "@/lib/questionSelect/selectableQuestion";
import { universityQuestionPracticeHref } from "@/lib/universityLink/practiceHref";
import PageTitleHeader from "@/components/shared/PageTitleHeader";

// スヌーズ状態（localStorage）は同一表示中に外部から変わらないため購読は no-op。
// 「あとで設定する」押下後は nudgeDismissed で閉じ、次回マウント時に最新値を読み直す。
const subscribeNudgeSnooze = () => () => {};

/* ─── Types ─── */

interface DashboardData {
  overall: {
    score: number;
    rank: string | null;
    delta_from_last_week: number;
    total_practice_count: number;
  };
  skills: { key: string; label: string; score: number; delta: number }[];
  trend: { date: string; score: number }[];
  categories: {
    weight_category: string;
    label: string;
    average_score: number;
    practice_count: number;
    questions: {
      question_id: number | null;
      question_text: string;
      latest_score: number;
      latest_rank: string;
    }[];
  }[];
  recommendation: {
    question_id: number | null;
    question_text: string;
    score: number;
    message: string;
  } | null;
}

/* ─── Helpers ─── */

function buildScoreMap(
  data: DashboardData
): Map<number, { score: number; rank: string }> {
  const map = new Map<number, { score: number; rank: string }>();
  for (const cat of data.categories) {
    for (const q of cat.questions) {
      const key = q.question_id ?? ALL_QUESTIONS.find((aq) => aq.text.trim() === q.question_text.trim())?.id;
      if (key != null) {
        map.set(key, {
          score: q.latest_score,
          rank: q.latest_rank,
        });
      }
    }
  }
  return map;
}

// API recommendation を questions.ts のマスタ Question に解決する。
// 表示テキストと遷移先 ID を必ず同じ Question から導出することで、
// 「カードに表示されたテキストと練習画面のテキストが食い違う」不整合を防ぐ（Issue #691）。
// 解決順序: (1) question_id でマスタ検索 → (2) question_text でマスタ検索 → (3) null。
// API の question_text は古い保存値の可能性があるため信頼せず、解決後の Question.text を表示に使う。
function resolveRecommendedQuestion(recommendation: {
  question_id: number | null;
  question_text: string;
}): Question | null {
  if (recommendation.question_id != null) {
    const byId = ALL_QUESTIONS.find((q) => q.id === recommendation.question_id);
    if (byId) return byId;
  }
  const byText = ALL_QUESTIONS.find(
    (q) => q.text.trim() === recommendation.question_text.trim()
  );
  return byText ?? null;
}

/* ─── Sub Components ─── */

// AI おすすめ練習カード（コーラル背景）。
// DESIGN.md §「カード」の card-header + card-body 構造に従う（区切りは white ボーダー）。
// ゲスト / ログイン×履歴なし / ログイン×履歴あり の3 variant をラベルとスコア表示で出し分ける。
function RecommendationCard({
  label,
  questionText,
  questionId,
  message,
  score,
  onSelect,
}: {
  label: string;
  questionText: string;
  questionId: number;
  message: string;
  score?: number;
  onSelect: (id: number) => void;
}) {
  return (
    <section className="bg-accent rounded-lg custom-shadow mb-6 overflow-hidden">
      {/* card-header */}
      <div className="border-b border-white p-4 md:p-5">
        <h2 className="text-white text-lg md:text-xl font-bold">
          {label}
        </h2>
      </div>

      {/* card-body */}
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {score != null && (
            <span className="inline-flex items-center bg-white text-accent text-xs md:text-base font-bold px-2 py-0.5 rounded font-[family-name:var(--font-roboto)]">
              前回 {score}点
            </span>
          )}
          <p className="text-base md:text-lg font-bold text-white leading-relaxed flex-1 min-w-0">
            {questionText}
          </p>
        </div>

        <p className="text-white text-sm md:text-base leading-relaxed mb-4">
          {message}
        </p>

        <div className="flex md:justify-end">
          <button
            onClick={() => onSelect(questionId)}
            className="w-full md:w-auto inline-flex items-center justify-center bg-white text-primary font-bold text-sm rounded-lg px-5 py-2.5 min-h-[40px] transition-colors hover:bg-white/95 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-accent"
          >
            練習を開始
          </button>
        </div>
      </div>
    </section>
  );
}

function RecommendationSkeleton() {
  return (
    <section className="bg-accent rounded-lg custom-shadow mb-6 overflow-hidden animate-pulse">
      <div className="border-b border-white p-4 md:p-5">
        <div className="h-5 md:h-6 w-56 bg-white/40 rounded" />
      </div>
      <div className="p-4 md:p-5">
        <div className="h-5 md:h-6 w-3/4 bg-white/40 rounded mb-3" />
        <div className="h-4 md:h-5 w-full bg-white/40 rounded mb-4" />
        <div className="h-10 w-full md:w-32 md:ml-auto bg-white/60 rounded-lg" />
      </div>
    </section>
  );
}

// 質問選択画面のソート行 UI（Issue #869 で実機能化）。
// ネイティブ `<select>` を使い、フォーカス順序・読み上げを標準で満たす。
// ログイン状態に応じて選択肢を出し分ける（未ログインは「おすすめ順」のみ）。
// 見た目は Figma 準拠の右寄せピル。ネイティブ矢印は隠し ChevronDown を重ねる。
function SortSelect({
  value,
  onChange,
  isLoggedIn,
}: {
  value: SortKey;
  onChange: (value: SortKey) => void;
  isLoggedIn: boolean;
}) {
  const options = availableSortOptions(isLoggedIn);
  return (
    <div className="flex justify-end mb-4">
      <div className="relative inline-flex items-center">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as SortKey)}
          aria-label="並び順"
          className="appearance-none bg-surface border border-gray-300 text-[13px] rounded-lg pl-3 pr-9 py-2 min-h-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-3 text-on-surface"
        />
      </div>
    </div>
  );
}

// 質問カード内のメタ情報バッジ。
// カテゴリ名と職種名（「〜向け」）を bg-white + border-gray-500 で並べる。
// `shared/QuestionBadges.tsx` の `JobTypeBadge`（teal）は /interview/select でのみ使う。
function MetaBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-white border border-gray-500 text-gray-500">
      {children}
    </span>
  );
}

function QuestionCard({
  question,
  scoreInfo,
  showUnpracticedBadge,
  isRecommended,
  onSelect,
}: {
  question: Question;
  scoreInfo: { score: number; rank: string } | null;
  showUnpracticedBadge: boolean;
  isRecommended?: boolean;
  onSelect: (id: number) => void;
}) {
  return (
    <button
      onClick={() => onSelect(question.id)}
      className={`relative w-full text-left bg-surface rounded-lg px-4 py-4 custom-shadow border-2 transition-all flex flex-col gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 hover:shadow-lg ${
        isRecommended ? "border-primary" : "border-transparent hover:border-accent/40"
      }`}
    >
      {isRecommended && (
        <span className="absolute -top-2 right-3 bg-primary text-white text-xs font-bold px-2.5 py-0.5 rounded-full shadow-sm">
          おすすめ
        </span>
      )}

      <div className="flex items-center gap-1.5 flex-wrap">
        <MetaBadge>{getDisplayCategory(question.category)}</MetaBadge>
        {question.job_type_keys.map((key) => (
          <MetaBadge key={key}>{getJobTypeLabel(key)}向け</MetaBadge>
        ))}
      </div>

      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm font-medium text-on-surface leading-relaxed">
          {question.text}
        </p>
        <ChevronRight
          className="shrink-0 self-center text-primary w-6 h-6 group-hover:text-primary-hover transition-colors"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {scoreInfo ? (
          <span className="inline-flex items-center bg-white text-accent border border-accent text-xs font-bold px-2 py-0.5 rounded-full font-[family-name:var(--font-roboto)]">
            {scoreInfo.score}点
          </span>
        ) : showUnpracticedBadge ? (
          <span className="inline-flex items-center bg-accent text-white text-xs font-semibold px-2 py-0.5 rounded-full">
            未実施
          </span>
        ) : null}
      </div>
    </button>
  );
}

// 大学が登録した練習用質問のカード（#1861）。共通質問のカードと同じ意匠で、
// 左上に赤い「大学専用」とカテゴリのラベルを置く。スコア・未実施のバッジは持たない（記録が無い）。
function UniversityQuestionCard({
  question,
  onSelect,
}: {
  question: UniversitySelectable;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(question.id)}
      data-testid="university-question-card"
      className="relative w-full text-left bg-surface rounded-lg px-4 py-4 custom-shadow border-2 border-transparent hover:border-accent/40 transition-all flex flex-col gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 hover:shadow-lg"
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <UniversityBadge />
        {question.category && <MetaBadge>{question.category}</MetaBadge>}
      </div>
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm font-medium text-on-surface leading-relaxed">{question.text}</p>
        <ChevronRight className="shrink-0 self-center text-primary w-6 h-6 group-hover:text-primary-hover transition-colors" />
      </div>
    </button>
  );
}

/* ─── Main Component ─── */

export default function QuestionSelectClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { showModal: limitReached } = usePracticeLimit();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [dashboardFetched, setDashboardFetched] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);
  // プロフィール設定誘導モーダル（Issue #1003）を閉じたか。「あとで設定する」押下時に true。
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // URL から state を初期化する。共有 URL や戻るボタン時の状態復元のため。
  // category はプラン §3 で URL 連動対象外（buildPracticeUrl も書かない）。
  // university は「大学専用」の行（#1861）。大学連携画面から ?university=all で遷移してくる。
  const initialJob = searchParams.get("job") ?? "all";
  const initialUniversity = searchParams.get("university");

  // 大学が登録した練習用質問。名簿に一致する学生だけが持ち、通常の質問より上に並べる（#1861）
  const { questions: universityQuestions } = useUniversityQuestions();

  // プロフィールの新卒/中途区分で質問一覧を絞り込む。ログイン済はプロフィール区分、
  // 未ログインは一時保存値を用いる（未解決・未設定時は全件表示）。
  // 取得済みプロフィールは誘導モーダルの完成度判定にも使い回し、/users/me の重複取得を避ける。
  const { careerStage, profile } = useCareerStage();

  const {
    jobFilter,
    setJobFilter,
    selectedCategory,
    setSelectedCategory,
    jobOptions,
    categoryOptions,
    filtered,
    filtersActive,
    resetFilters,
    universityCategory,
    setUniversityCategory,
    universityOptions,
    universityFiltered,
  } = useQuestionFilters({ initialJob, careerStage, universityQuestions, initialUniversity });

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;

    apiClient
      .get("/users/dashboard", { timeout: 3000 })
      .then((res) => {
        if (!cancelled) setDashboardData(res.data);
      })
      .catch(() => {
        // Fallback: dashboardData stays null
      })
      .finally(() => {
        if (!cancelled) setDashboardFetched(true);
      });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  // スヌーズ期限内かは localStorage 由来。マウント後に読むため useSyncExternalStore を使い、
  // SSR/ハイドレーション時は true（＝表示可、ただし profile 未取得のため実際には非表示）に固定する。
  const canShowNudge = useSyncExternalStore(
    subscribeNudgeSnooze,
    () => canShowProfileNudge(),
    () => true
  );

  // プロフィール誘導モーダル（Issue #1003）は副作用ではなく派生値で表示可否を決める。
  // 取得済みプロフィール（useCareerStage 経由）の完成度・スヌーズ・閉じ操作から算出する。
  const completeness = profile?.completeness;
  const profileNudgeOpen =
    !!user &&
    canShowNudge &&
    !nudgeDismissed &&
    !!completeness &&
    completeness.filled < completeness.total;

  const handleProfileNudgeClose = () => {
    snoozeProfileNudge();
    setNudgeDismissed(true);
  };

  const apiLoading = !!user && !authLoading && !dashboardFetched;

  const handleSelect = (id: number) => {
    router.push(
      buildPracticeUrl({
        questionId: id,
        career: careerStage === "high_school" ? "high_school" : "all",
        job: jobFilter,
      }),
    );
  };

  const handleSelectUniversity = (id: string) => {
    router.push(universityQuestionPracticeHref(id));
  };

  // フィルタ chip 切替時に URL クエリ（job / university）を同期する。
  // 共有 / ブックマーク / 戻る・進むの体験を URL で表現する目的（Issue #692）。
  // 履歴に積むと chip 連打で「戻る」連打を強いるため router.replace を採用し、
  // チップ列のスクロール位置を保つため { scroll: false } を渡す。
  // job と university は排他（フック側で片方を選ぶと他方が戻る）なので、どちらか一方だけが載る。
  const replaceFilterUrl = (nextJob: string, nextUniversity: string | null) => {
    const params = new URLSearchParams();
    if (nextJob && nextJob !== "all") params.set("job", nextJob);
    if (nextUniversity) params.set("university", nextUniversity);
    const qs = params.toString();
    router.replace(qs ? `/practice/select?${qs}` : "/practice/select", { scroll: false });
  };

  const handleJobChange = (value: string) => {
    setJobFilter(value);
    replaceFilterUrl(value, null);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    // カテゴリは URL 連動対象外だが、大学専用を選んでいたときはその選択が外れるので
    // URL の university だけは落とす
    if (universityCategory !== null) replaceFilterUrl(jobFilter, null);
  };

  const handleUniversityChange = (value: string | null) => {
    setUniversityCategory(value);
    replaceFilterUrl("all", value);
  };

  const scoreMap = dashboardData ? buildScoreMap(dashboardData) : null;
  const hasPracticeHistory =
    dashboardData != null && dashboardData.overall.total_practice_count > 0;

  const getScoreInfo = (question: Question) => {
    if (!scoreMap) return null;
    return scoreMap.get(question.id) ?? null;
  };

  // 「未実施」バッジは「ログイン済 + 練習履歴あり + その質問は未練習」のときに表示する。
  // ゲストや履歴ゼロのユーザーには出さない（全質問が未練習なので情報量がない）。
  const shouldShowUnpracticed = (question: Question) => {
    if (!user || !hasPracticeHistory || !scoreMap) return false;
    return !scoreMap.has(question.id);
  };

  const isRecommendationLoading = !!user && apiLoading;
  // 区分の可視範囲に含まれる質問集合（職種・カテゴリの絞り込み前）。
  // おすすめが区分可視範囲外だと一覧に該当カードが無くハイライトも付かないため、
  // おすすめ解決結果をこの集合でゲートし、範囲外なら静的フォールバックへ倒す。
  const careerVisibleIds = new Set(
    filterQuestionsByCareerStage(ALL_QUESTIONS, careerStage).map((q) => q.id)
  );
  // API recommendation を questions.ts マスタに解決した結果。
  // 解決できない（マスタ未存在の id + text 不一致）場合や区分可視範囲外の場合は null となり、
  // 静的フォールバックカードが表示される。
  const resolvedRecommendationRaw =
    dashboardData?.recommendation != null && hasPracticeHistory
      ? resolveRecommendedQuestion(dashboardData.recommendation)
      : null;
  const resolvedRecommendation =
    resolvedRecommendationRaw != null &&
    careerVisibleIds.has(resolvedRecommendationRaw.id)
      ? resolvedRecommendationRaw
      : null;

  const fallbackQuestion =
    filterQuestionsByCareerStage(ALL_QUESTIONS, careerStage)[0] ??
    GENERAL_QUESTIONS[0];
  const recommendedQuestionId: number =
    resolvedRecommendation?.id ?? fallbackQuestion.id;

  // フィルタ済み一覧を選択中の基準で並べ替える。スコア未取得時（scoreMap=null）は
  // sortQuestions 側で標準（おすすめ）順にフォールバックする。
  // メモ化は React Compiler に委ねる（手動 useMemo は scoreMap が毎レンダリング再生成される
  // 不安定な依存のため preserve-manual-memoization に抵触する）。
  const sortedQuestions = sortQuestions(filtered, {
    sortKey,
    scoreMap,
    recommendedId: recommendedQuestionId,
  });

  const handleResetFilters = () => {
    resetFilters();
    router.replace("/practice/select", { scroll: false });
  };

  return (
    <>
      {/* Page Title Header — dashboard と共通の全幅ストリップ（DESIGN.md §「ページタイトルヘッダー（共通）」）。
          画像のみ Figma 準拠で standing04.png に差し替える（共通構造は同じ）。 */}
      <PageTitleHeader
        imageSrc="/images/mentai-senpai_03_standing04.png"
        title="質問を選ぶ"
        subtitle="タップして練習を始めましょう"
        containerClassName="max-w-6xl mx-auto px-4 py-6 md:py-10"
      />

      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        {/* AI Recommendation Card — shown to all users */}
        {isRecommendationLoading ? (
          <RecommendationSkeleton />
        ) : !user ? (
          <RecommendationCard
            label="最初の1問はこれがおすすめ"
            questionText={fallbackQuestion.text}
            questionId={fallbackQuestion.id}
            message="面接の最初に必ず聞かれる質問です。まずはここから練習を始めましょう。"
            onSelect={handleSelect}
          />
        ) : resolvedRecommendation ? (
          <RecommendationCard
            label="プロフィールに基づいたAIおすすめ練習"
            questionText={resolvedRecommendation.text}
            questionId={resolvedRecommendation.id}
            message={dashboardData!.recommendation!.message}
            score={dashboardData!.recommendation!.score}
            onSelect={handleSelect}
          />
        ) : (
          <RecommendationCard
            label="まずはここから始めましょう"
            questionText={fallbackQuestion.text}
            questionId={fallbackQuestion.id}
            message="基本的な質問から始めるのが効果的です。練習するほど、あなたに合ったおすすめが表示されます。"
            onSelect={handleSelect}
          />
        )}

        <QuestionFilterPanel
          jobFilter={jobFilter}
          onJobChange={handleJobChange}
          jobOptions={jobOptions}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          categoryOptions={categoryOptions}
          resultCount={universityFiltered.length + filtered.length}
          filtersActive={filtersActive}
          onReset={handleResetFilters}
          universityCategory={universityCategory}
          onUniversityChange={handleUniversityChange}
          universityOptions={universityOptions}
        />

        {/* Sort UI（Issue #869）。並べ替える対象が無い 0 件時は非表示にする。 */}
        {filtered.length > 0 && (
          <SortSelect
            value={sortKey}
            onChange={setSortKey}
            isLoggedIn={!!user}
          />
        )}

        {/* Empty State */}
        {filtered.length === 0 && universityFiltered.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-12">
            <p className="text-center text-sm">
              この条件に該当する質問はありません
            </p>
            {filtersActive && (
              <button
                onClick={resetFilters}
                className="text-sm font-semibold text-white bg-primary hover:bg-primary-hover px-5 py-2.5 min-h-[44px] rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                フィルタをリセットする
              </button>
            )}
          </div>
        )}

        {/* Question grid。大学が登録した質問を先頭に置き、通常の質問を並び替え済みの順で続ける（#1861） */}
        {(filtered.length > 0 || universityFiltered.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-8">
            {universityFiltered.map((q) => (
              <UniversityQuestionCard key={q.key} question={q} onSelect={handleSelectUniversity} />
            ))}
            {sortedQuestions.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                scoreInfo={getScoreInfo(q)}
                showUnpracticedBadge={shouldShowUnpracticed(q)}
                isRecommended={q.id === recommendedQuestionId}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}

        {/* 練習回数制限モーダル（ゲストユーザー向け） */}
        {limitReached && <PracticeLimitModal />}
      </div>

      {/* プロフィール設定誘導モーダル（Issue #1003） */}
      <ProfileNudgeModal
        open={profileNudgeOpen}
        onClose={handleProfileNudgeClose}
      />
    </>
  );
}
